import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextLazy';
import { supabase } from '../../lib/supabase-auth';
import { colors } from '../../lib/colors';
import {
  ArrowLeftIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  PencilSquareIcon,
  UserIcon,
  PlayIcon,
  PauseIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftEllipsisIcon,
  BanknotesIcon,
  ListBulletIcon,
  XMarkIcon,
  TrashIcon,
  ScaleIcon,
  CalculatorIcon,
  UserGroupIcon,
  CogIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  InboxArrowDownIcon,
  PaperAirplaneIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon, ExclamationTriangleIcon as ExclamationTriangleSolidIcon } from '@heroicons/react/24/solid';

// Types
interface ProjectParty {
  party_id: string;
  role: string;
  is_primary: boolean;
  party: {
    id: string;
    name: string;
    type: string;
    primary_email: string | null;
  };
}

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  phase: number;
  sort_order: number;
  category: string | null;
  owner_type: 'advisor' | 'client' | 'legal' | 'accountant' | 'external' | 'system';
  assigned_to_party_id: string | null;
  assigned_to_user_id: string | null;
  status: 'not_started' | 'in_progress' | 'blocked' | 'complete' | 'cancelled' | 'na';
  blocked_reason: string | null;
  due_date: string | null;
  start_date: string | null;
  completed_at: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  depends_on_task_id: string | null;
  notes: string | null;
  template_task_id: string | null;
  created_at: string;
  updated_at: string;
  completed_by: string | null;
}

interface ProjectDocument {
  id: string;
  project_id: string;
  document_type: string;
  document_name: string;
  direction: 'from_client' | 'to_client';
  is_required: boolean;
  status: 'pending' | 'requested' | 'received' | 'reviewed' | 'approved' | 'rejected' | 'delivered' | 'na';
  file_path: string | null;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  file_url: string | null;
  version: number;
  previous_version_id: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_notes: string | null;
  rejection_reason: string | null;
  uploaded_at: string | null;
  uploaded_by_party_id: string | null;
  uploaded_by_user_id: string | null;
  due_date: string | null;
  requested_at: string | null;
  notes: string | null;
  template_doc_id: string | null;
  created_at: string;
  updated_at: string;
}

interface Payment {
  id: string;
  project_id: string;
  payment_type: 'deposit' | 'balance' | 'milestone' | 'additional' | 'refund';
  description: string | null;
  amount: number;
  currency: string;
  status: 'pending' | 'invoiced' | 'paid' | 'overdue' | 'waived' | 'refunded';
  due_date: string | null;
  invoiced_at: string | null;
  paid_at: string | null;
  payment_method: 'etransfer' | 'cheque' | 'wire' | 'credit_card' | 'cash' | 'other' | null;
  reference_number: string | null;
  receipt_number: string | null;
  invoice_number: string | null;
  invoice_file_path: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

interface ProjectNote {
  id: string;
  note_type: string;
  subject: string | null;
  content: string;
  is_internal: boolean;
  occurred_at: string;
  created_by: string;
  creator?: {
    full_name: string;
  };
}

interface Engagement {
  id: string;
  project_number: string | null;
  name: string;
  description: string | null;
  status: string;
  project_type: string | null;
  current_phase: number;
  total_phases: number;
  fee_total: number | null;
  fee_deposit: number | null;
  fee_paid: number | null;
  fee_currency: string;
  tasks_total: number;
  tasks_completed: number;
  docs_required: number;
  docs_received: number;
  engaged_date: string | null;
  target_completion_date: string | null;
  created_at: string;
  internal_notes: string | null;
  assigned_to: string | null;
  assignee?: {
    id: string;
    full_name: string;
  };
  project_parties?: ProjectParty[];
  template?: {
    name: string;
    phases: Array<{ number: number; name: string }>;
  };
}

// Tab configuration
type TabId = 'overview' | 'plan' | 'documents' | 'money' | 'worklog';

const TABS: { id: TabId; label: string; icon: React.ComponentType<any> }[] = [
  { id: 'overview', label: 'Overview', icon: FolderIcon },
  { id: 'plan', label: 'The Plan', icon: ClipboardDocumentListIcon },
  { id: 'documents', label: 'Documents', icon: DocumentTextIcon },
  { id: 'money', label: 'Money', icon: BanknotesIcon },
  { id: 'worklog', label: 'Work Log', icon: ChatBubbleLeftEllipsisIcon },
];

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    proposal: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Proposal' },
    engaged: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Engaged' },
    in_progress: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'In Progress' },
    on_hold: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'On Hold' },
    complete: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complete' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
  };

  const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Progress Stepper Component
const ProgressStepper: React.FC<{
  currentPhase: number;
  totalPhases: number;
  phases: Array<{ number: number; name: string }>;
}> = ({ currentPhase, totalPhases, phases }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-medium text-gray-900 mb-4">Project Phases</h3>
      <div className="relative">
        {/* Progress line */}
        <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200">
          <div
            className="h-full bg-blue-600 transition-all duration-500"
            style={{ width: `${((currentPhase - 1) / Math.max(totalPhases - 1, 1)) * 100}%` }}
          />
        </div>

        {/* Phase markers */}
        <div className="relative flex justify-between">
          {phases.map((phase) => {
            const isComplete = phase.number < currentPhase;
            const isCurrent = phase.number === currentPhase;
            const isPending = phase.number > currentPhase;

            return (
              <div key={phase.number} className="flex flex-col items-center">
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    transition-all duration-300
                    ${isComplete ? 'bg-blue-600 text-white' : ''}
                    ${isCurrent ? 'bg-blue-600 text-white ring-4 ring-blue-100' : ''}
                    ${isPending ? 'bg-white border-2 border-gray-300 text-gray-500' : ''}
                  `}
                >
                  {isComplete ? (
                    <CheckCircleIcon className="h-5 w-5" />
                  ) : (
                    phase.number
                  )}
                </div>
                <span
                  className={`
                    mt-2 text-xs font-medium text-center max-w-[100px]
                    ${isCurrent ? 'text-blue-600' : 'text-gray-500'}
                  `}
                >
                  {phase.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Quick Stats Component
const QuickStats: React.FC<{
  engagement: Engagement;
  tasks: Task[];
  documents: ProjectDocument[];
  payments: Payment[];
  onStatClick?: (tab: 'plan' | 'documents' | 'money') => void;
}> = ({ engagement, tasks, documents, payments, onStatClick }) => {
  const completedTasks = tasks.filter(t => t.status === 'complete').length;
  const totalTasks = tasks.length;
  const taskProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const receivedDocs = documents.filter(d => ['received', 'reviewed', 'approved'].includes(d.status)).length;
  const requiredDocs = documents.filter(d => d.is_required && d.direction === 'from_client').length;
  const docProgress = requiredDocs > 0 ? Math.round((receivedDocs / requiredDocs) * 100) : 100;

  const totalFees = engagement.fee_total || 0;
  const paidAmount = engagement.fee_paid || 0;
  const paymentProgress = totalFees > 0 ? Math.round((paidAmount / totalFees) * 100) : 0;

  const stats = [
    {
      label: 'Tasks',
      value: `${completedTasks}/${totalTasks}`,
      progress: taskProgress,
      color: 'blue',
      icon: CheckCircleIcon,
      tab: 'plan' as const,
    },
    {
      label: 'Documents',
      value: `${receivedDocs}/${requiredDocs}`,
      progress: docProgress,
      color: 'purple',
      icon: DocumentTextIcon,
      tab: 'documents' as const,
    },
    {
      label: 'Payment',
      value: `$${paidAmount.toLocaleString()}`,
      subtitle: `of $${totalFees.toLocaleString()}`,
      progress: paymentProgress,
      color: 'green',
      icon: CurrencyDollarIcon,
      tab: 'money' as const,
    },
  ];

  const colorClasses: Record<string, { bg: string; bar: string; text: string }> = {
    blue: { bg: 'bg-blue-100', bar: 'bg-blue-600', text: 'text-blue-600' },
    purple: { bg: 'bg-purple-100', bar: 'bg-purple-600', text: 'text-purple-600' },
    green: { bg: 'bg-green-100', bar: 'bg-green-600', text: 'text-green-600' },
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {stats.map((stat) => {
        const colors = colorClasses[stat.color];
        return (
          <div
            key={stat.label}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all"
            onClick={() => onStatClick?.(stat.tab)}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">{stat.label}</span>
              <stat.icon className={`h-5 w-5 ${colors.text}`} />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
              {stat.subtitle && (
                <span className="text-sm text-gray-500">{stat.subtitle}</span>
              )}
            </div>
            <div className="mt-2">
              <div className={`h-2 rounded-full ${colors.bg}`}>
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${colors.bar}`}
                  style={{ width: `${stat.progress}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 mt-1">{stat.progress}% complete</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Next Actions Component
const NextActions: React.FC<{ tasks: Task[]; onTaskClick?: (taskId: string) => void }> = ({ tasks, onTaskClick }) => {
  // Get incomplete tasks sorted by due date and priority
  const nextTasks = tasks
    .filter(t => t.status !== 'complete' && t.status !== 'cancelled' && t.status !== 'na')
    .sort((a, b) => {
      // Priority order
      const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
      const priorityDiff = (priorityOrder[a.priority as keyof typeof priorityOrder] || 2) -
                          (priorityOrder[b.priority as keyof typeof priorityOrder] || 2);
      if (priorityDiff !== 0) return priorityDiff;

      // Then by due date
      if (a.due_date && b.due_date) {
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      }
      return a.due_date ? -1 : 1;
    })
    .slice(0, 5);

  const priorityColors: Record<string, string> = {
    urgent: 'border-l-red-500 bg-red-50',
    high: 'border-l-orange-500 bg-orange-50',
    normal: 'border-l-blue-500 bg-blue-50',
    low: 'border-l-gray-400 bg-gray-50',
  };

  const ownerIcons: Record<string, { icon: React.ComponentType<any>; color: string }> = {
    client: { icon: UserIcon, color: 'text-purple-600' },
    advisor: { icon: PencilSquareIcon, color: 'text-blue-600' },
    legal: { icon: DocumentTextIcon, color: 'text-amber-600' },
    accountant: { icon: CurrencyDollarIcon, color: 'text-green-600' },
    external: { icon: UserIcon, color: 'text-gray-600' },
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">Next Actions</h3>
        <span className="text-xs text-gray-500">{nextTasks.length} pending</span>
      </div>

      {nextTasks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <CheckCircleSolidIcon className="h-12 w-12 mx-auto text-green-500 mb-2" />
          <p className="text-sm">All tasks complete!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {nextTasks.map((task) => {
            const ownerConfig = ownerIcons[task.owner_type] || ownerIcons.external;
            const isOverdue = task.due_date && new Date(task.due_date) < new Date();

            return (
              <div
                key={task.id}
                className={`
                  border-l-4 rounded-r-lg p-3 cursor-pointer hover:shadow-md transition-shadow
                  ${priorityColors[task.priority] || priorityColors.normal}
                `}
                onClick={() => onTaskClick?.(task.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <ownerConfig.icon className={`h-4 w-4 ${ownerConfig.color}`} />
                      <span className="text-xs text-gray-500 capitalize">{task.owner_type}</span>
                      {task.due_date && (
                        <>
                          <span className="text-gray-300">|</span>
                          <CalendarDaysIcon className={`h-4 w-4 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`} />
                          <span className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Key Dates Component
const KeyDates: React.FC<{ engagement: Engagement }> = ({ engagement }) => {
  const dates = [
    {
      label: 'Engaged',
      date: engagement.engaged_date,
      icon: PlayIcon,
      color: 'text-green-600',
    },
    {
      label: 'Target Completion',
      date: engagement.target_completion_date,
      icon: CalendarDaysIcon,
      color: 'text-blue-600',
    },
    {
      label: 'Created',
      date: engagement.created_at,
      icon: ClockIcon,
      color: 'text-gray-600',
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-sm font-medium text-gray-900 mb-4">Key Dates</h3>
      <div className="space-y-4">
        {dates.map((item) => (
          <div key={item.label} className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-gray-100`}>
              <item.icon className={`h-4 w-4 ${item.color}`} />
            </div>
            <div>
              <p className="text-xs text-gray-500">{item.label}</p>
              <p className="text-sm font-medium text-gray-900">
                {item.date ? new Date(item.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }) : 'Not set'}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Activity Feed Component
const ActivityFeed: React.FC<{ notes: ProjectNote[] }> = ({ notes }) => {
  const recentNotes = notes.slice(0, 5);

  const noteTypeConfig: Record<string, { icon: React.ComponentType<any>; color: string }> = {
    meeting: { icon: UserIcon, color: 'bg-blue-100 text-blue-600' },
    call: { icon: ChatBubbleLeftEllipsisIcon, color: 'bg-green-100 text-green-600' },
    email: { icon: DocumentTextIcon, color: 'bg-purple-100 text-purple-600' },
    internal: { icon: PencilSquareIcon, color: 'bg-gray-100 text-gray-600' },
    decision: { icon: CheckCircleIcon, color: 'bg-amber-100 text-amber-600' },
    milestone: { icon: FolderIcon, color: 'bg-indigo-100 text-indigo-600' },
    update: { icon: ClockIcon, color: 'bg-cyan-100 text-cyan-600' },
    issue: { icon: ExclamationTriangleIcon, color: 'bg-red-100 text-red-600' },
    resolution: { icon: CheckCircleSolidIcon, color: 'bg-green-100 text-green-600' },
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">Recent Activity</h3>
        <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
          View all
        </button>
      </div>

      {recentNotes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <ChatBubbleLeftEllipsisIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
          <p className="text-sm">No activity yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {recentNotes.map((note) => {
            const config = noteTypeConfig[note.note_type] || noteTypeConfig.update;
            const Icon = config.icon;

            return (
              <div key={note.id} className="flex gap-3">
                <div className={`p-2 rounded-lg flex-shrink-0 ${config.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {note.subject || note.note_type.replace('_', ' ')}
                  </p>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                    {note.content}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {note.creator?.full_name || 'System'} - {new Date(note.occurred_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main Component
export const EngagementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useAuth();

  // State
  const [engagement, setEngagement] = useState<Engagement | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [documents, setDocuments] = useState<ProjectDocument[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Plan tab state
  const [collapsedPhases, setCollapsedPhases] = useState<Set<number>>(new Set());
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [addTaskPhase, setAddTaskPhase] = useState<number>(1);
  const [taskUpdating, setTaskUpdating] = useState<string | null>(null);
  const [deleteConfirmTask, setDeleteConfirmTask] = useState<Task | null>(null);

  // Documents tab state
  const [documentUpdating, setDocumentUpdating] = useState<string | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState<ProjectDocument | null>(null);
  const [editingDocument, setEditingDocument] = useState<ProjectDocument | null>(null);
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [addDocumentDirection, setAddDocumentDirection] = useState<'from_client' | 'to_client'>('from_client');
  const [collapsedDocSections, setCollapsedDocSections] = useState<Set<string>>(new Set());

  // Money tab state
  const [paymentUpdating, setPaymentUpdating] = useState<string | null>(null);
  const [markPaidPayment, setMarkPaidPayment] = useState<Payment | null>(null);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);

  // Work Log tab state
  const [noteFilter, setNoteFilter] = useState<string>('all');
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState<ProjectNote | null>(null);
  const [deleteConfirmNote, setDeleteConfirmNote] = useState<ProjectNote | null>(null);
  const [noteUpdating, setNoteUpdating] = useState<string | null>(null);

  // Fetch engagement data
  const fetchEngagement = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Fetch engagement with related data
      // Note: template join removed due to missing FK relationship - phases from template stored in project
      const { data: engagementData, error: engagementError } = await supabase
        .from('projects')
        .select('*, assignee:platform_users!assigned_to(id,full_name), project_parties(party_id,role,is_primary,party:parties(id,name,type,primary_email))')
        .eq('id', id)
        .single();

      if (engagementError) throw engagementError;
      setEngagement(engagementData);

      // Fetch tasks
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('*')
        .eq('project_id', id)
        .order('phase', { ascending: true })
        .order('sort_order', { ascending: true });

      if (!tasksError) setTasks(tasksData || []);

      // Fetch documents
      const { data: docsData, error: docsError } = await supabase
        .from('project_documents')
        .select('*')
        .eq('project_id', id)
        .order('uploaded_at', { ascending: false });

      if (!docsError) setDocuments(docsData || []);

      // Fetch payments
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false });

      if (!paymentsError) setPayments(paymentsData || []);

      // Fetch notes
      const { data: notesData, error: notesError } = await supabase
        .from('project_notes')
        .select('*, creator:platform_users!created_by(full_name)')
        .eq('project_id', id)
        .order('occurred_at', { ascending: false });

      if (!notesError) setNotes(notesData || []);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching engagement:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEngagement();
  }, [fetchEngagement]);

  // Task management functions
  const togglePhaseCollapse = (phaseNumber: number) => {
    setCollapsedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phaseNumber)) {
        next.delete(phaseNumber);
      } else {
        next.add(phaseNumber);
      }
      return next;
    });
  };

  const updateTaskStatus = async (taskId: string, newStatus: Task['status']) => {
    if (!id || taskUpdating) return;

    setTaskUpdating(taskId);
    try {
      const updateData: Partial<Task> = {
        status: newStatus,
        completed_at: newStatus === 'complete' ? new Date().toISOString() : null,
        completed_by: newStatus === 'complete' ? userProfile?.id || null : null,
      };

      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', taskId);

      if (error) throw error;

      // Update local state
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, ...updateData } : t
      ));

      // Update project counters
      const updatedTasks = tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t);
      const completedCount = updatedTasks.filter(t => t.status === 'complete').length;

      await supabase
        .from('projects')
        .update({
          tasks_completed: completedCount,
          tasks_total: updatedTasks.length,
        })
        .eq('id', id);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating task:', error);
      }
    } finally {
      setTaskUpdating(null);
    }
  };

  const createTask = async (taskData: Partial<Task>) => {
    if (!id) return;

    try {
      const maxSortOrder = tasks
        .filter(t => t.phase === taskData.phase)
        .reduce((max, t) => Math.max(max, t.sort_order), 0);

      const { data, error } = await supabase
        .from('tasks')
        .insert({
          project_id: id,
          title: taskData.title,
          description: taskData.description || null,
          phase: taskData.phase || 1,
          sort_order: maxSortOrder + 1,
          owner_type: taskData.owner_type || 'advisor',
          status: taskData.status || 'not_started',
          priority: taskData.priority || 'normal',
          due_date: taskData.due_date || null,
          category: taskData.category || null,
          notes: taskData.notes || null,
        })
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => [...prev, data]);
      setShowAddTaskModal(false);

      // Update project task count
      await supabase
        .from('projects')
        .update({ tasks_total: tasks.length + 1 })
        .eq('id', id);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating task:', error);
      }
    }
  };

  const updateTask = async (taskId: string, taskData: Partial<Task>) => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          title: taskData.title,
          description: taskData.description,
          owner_type: taskData.owner_type,
          status: taskData.status,
          priority: taskData.priority,
          due_date: taskData.due_date,
          notes: taskData.notes,
          blocked_reason: taskData.blocked_reason,
        })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, ...taskData } : t
      ));
      setEditingTask(null);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating task:', error);
      }
    }
  };

  const deleteTask = async (taskId: string) => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      const newTasks = tasks.filter(t => t.id !== taskId);
      setTasks(newTasks);
      setDeleteConfirmTask(null);

      // Update project task counts
      const completedCount = newTasks.filter(t => t.status === 'complete').length;
      await supabase
        .from('projects')
        .update({
          tasks_total: newTasks.length,
          tasks_completed: completedCount,
        })
        .eq('id', id);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting task:', error);
      }
    }
  };

  // Document management functions
  const toggleDocSectionCollapse = (section: string) => {
    setCollapsedDocSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const updateDocumentStatus = async (docId: string, newStatus: ProjectDocument['status']) => {
    if (!id || documentUpdating) return;

    setDocumentUpdating(docId);
    try {
      const updateData: Partial<ProjectDocument> = {
        status: newStatus,
        ...(newStatus === 'requested' ? { requested_at: new Date().toISOString() } : {}),
      };

      const { error } = await supabase
        .from('project_documents')
        .update(updateData)
        .eq('id', docId);

      if (error) throw error;

      // Update local state
      setDocuments(prev => prev.map(d =>
        d.id === docId ? { ...d, ...updateData } : d
      ));

      // Update project document counts
      const updatedDocs = documents.map(d => d.id === docId ? { ...d, status: newStatus } : d);
      const receivedCount = updatedDocs.filter(d =>
        d.direction === 'from_client' &&
        ['received', 'reviewed', 'approved'].includes(d.status) &&
        d.status !== 'na'
      ).length;
      const requiredCount = updatedDocs.filter(d =>
        d.direction === 'from_client' &&
        d.is_required &&
        d.status !== 'na'
      ).length;

      await supabase
        .from('projects')
        .update({
          docs_received: receivedCount,
          docs_required: requiredCount,
        })
        .eq('id', id);

    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating document:', error);
      }
    } finally {
      setDocumentUpdating(null);
    }
  };

  const updateDocumentNotes = async (docId: string, notes: string) => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('project_documents')
        .update({ notes })
        .eq('id', docId);

      if (error) throw error;

      setDocuments(prev => prev.map(d =>
        d.id === docId ? { ...d, notes } : d
      ));
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating document notes:', error);
      }
    }
  };

  const handleFileUpload = async (docId: string, file: File) => {
    if (!id || !userProfile?.id) return;

    // File size validation (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      alert('File size must be less than 10MB. Please choose a smaller file.');
      return;
    }

    // File type validation
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/gif',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a PDF, image (JPEG, PNG, GIF), Word document, Excel spreadsheet, or CSV file.');
      return;
    }

    setDocumentUpdating(docId);
    try {
      // Create file path: project_id/document_id/timestamp_filename for uniqueness
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${id}/${docId}/${timestamp}_${sanitizedFileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('project-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get the public URL (or signed URL for private buckets)
      const { data: urlData } = await supabase.storage
        .from('project-documents')
        .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry

      // Update document record
      const updateData = {
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        file_url: urlData?.signedUrl || null,
        uploaded_at: new Date().toISOString(),
        uploaded_by_user_id: userProfile.id,
        status: 'received' as const,
      };

      const { error: updateError } = await supabase
        .from('project_documents')
        .update(updateData)
        .eq('id', docId);

      if (updateError) throw updateError;

      // Update local state
      setDocuments(prev => prev.map(d =>
        d.id === docId ? { ...d, ...updateData } : d
      ));

      // Update project document counts
      const updatedDocs = documents.map(d => d.id === docId ? { ...d, status: 'received' } : d);
      const receivedCount = updatedDocs.filter(d =>
        d.direction === 'from_client' &&
        ['received', 'reviewed', 'approved'].includes(d.status)
      ).length;

      await supabase
        .from('projects')
        .update({ docs_received: receivedCount })
        .eq('id', id);

      setUploadingDocument(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error uploading file:', error);
      }
    } finally {
      setDocumentUpdating(null);
    }
  };

  const downloadDocument = async (doc: ProjectDocument) => {
    if (!doc.file_path) return;

    try {
      // Get fresh signed URL
      const { data, error } = await supabase.storage
        .from('project-documents')
        .createSignedUrl(doc.file_path, 60 * 60); // 1 hour

      if (error) throw error;

      // Open in new tab
      window.open(data.signedUrl, '_blank');
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error downloading document:', error);
      }
    }
  };

  const createDocument = async (docData: Partial<ProjectDocument>) => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('project_documents')
        .insert({
          project_id: id,
          document_type: docData.document_type || 'other',
          document_name: docData.document_name,
          direction: docData.direction || 'from_client',
          is_required: docData.is_required ?? true,
          status: 'pending',
          due_date: docData.due_date || null,
          notes: docData.notes || null,
        })
        .select()
        .single();

      if (error) throw error;

      setDocuments(prev => [...prev, data]);
      setShowAddDocumentModal(false);

      // Update project document count if required
      if (data.is_required && data.direction === 'from_client') {
        await supabase
          .from('projects')
          .update({ docs_required: documents.filter(d => d.is_required && d.direction === 'from_client').length + 1 })
          .eq('id', id);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating document:', error);
      }
    }
  };

  // Payment management functions
  const markPaymentPaid = async (paymentId: string, paidData: { paid_at: string; payment_method: Payment['payment_method']; reference_number?: string; notes?: string }) => {
    if (!id || paymentUpdating) return;

    setPaymentUpdating(paymentId);
    try {
      const { error } = await supabase
        .from('payments')
        .update({
          status: 'paid',
          paid_at: paidData.paid_at,
          payment_method: paidData.payment_method,
          reference_number: paidData.reference_number || null,
          notes: paidData.notes || null,
        })
        .eq('id', paymentId);

      if (error) throw error;

      // Update local state
      const payment = payments.find(p => p.id === paymentId);
      setPayments(prev => prev.map(p =>
        p.id === paymentId
          ? { ...p, status: 'paid', paid_at: paidData.paid_at, payment_method: paidData.payment_method, reference_number: paidData.reference_number || null, notes: paidData.notes || null }
          : p
      ));

      // Refresh engagement to get updated fee_paid from trigger
      const { data: updatedEngagement } = await supabase
        .from('projects')
        .select('fee_paid')
        .eq('id', id)
        .single();

      if (updatedEngagement && engagement) {
        setEngagement({ ...engagement, fee_paid: updatedEngagement.fee_paid });
      }

      setMarkPaidPayment(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error marking payment as paid:', error);
      }
    } finally {
      setPaymentUpdating(null);
    }
  };

  const updatePaymentStatus = async (paymentId: string, newStatus: Payment['status']) => {
    if (!id || paymentUpdating) return;

    setPaymentUpdating(paymentId);
    try {
      const updateData: Partial<Payment> = { status: newStatus };
      if (newStatus !== 'paid') {
        updateData.paid_at = null;
        updateData.payment_method = null;
        updateData.reference_number = null;
      }

      const { error } = await supabase
        .from('payments')
        .update(updateData)
        .eq('id', paymentId);

      if (error) throw error;

      setPayments(prev => prev.map(p =>
        p.id === paymentId ? { ...p, ...updateData } : p
      ));

      // Refresh engagement to get updated fee_paid from trigger
      const { data: updatedEngagement } = await supabase
        .from('projects')
        .select('fee_paid')
        .eq('id', id)
        .single();

      if (updatedEngagement && engagement) {
        setEngagement({ ...engagement, fee_paid: updatedEngagement.fee_paid });
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating payment status:', error);
      }
    } finally {
      setPaymentUpdating(null);
    }
  };

  const updatePayment = async (paymentId: string, updateData: Partial<Payment>) => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('payments')
        .update({
          description: updateData.description,
          amount: updateData.amount,
          due_date: updateData.due_date || null,
          notes: updateData.notes || null,
        })
        .eq('id', paymentId);

      if (error) throw error;

      setPayments(prev => prev.map(p =>
        p.id === paymentId ? { ...p, ...updateData } : p
      ));

      // Refresh engagement to update fee totals
      await fetchEngagement();

      setEditingPayment(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating payment:', error);
      }
    }
  };

  const createPayment = async (paymentData: Partial<Payment>) => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('payments')
        .insert({
          project_id: id,
          payment_type: paymentData.payment_type || 'additional',
          description: paymentData.description || null,
          amount: paymentData.amount || 0,
          currency: 'CAD',
          status: 'pending',
          due_date: paymentData.due_date || null,
          notes: paymentData.notes || null,
          created_by: userProfile?.id || null,
        })
        .select()
        .single();

      if (error) throw error;

      setPayments(prev => [...prev, data]);
      setShowAddPaymentModal(false);

      // Refresh engagement to update totals
      await fetchEngagement();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating payment:', error);
      }
    }
  };

  const deletePayment = async (paymentId: string) => {
    if (!id) return;

    try {
      const { error } = await supabase
        .from('payments')
        .delete()
        .eq('id', paymentId);

      if (error) throw error;

      setPayments(prev => prev.filter(p => p.id !== paymentId));

      // Refresh engagement to update totals
      await fetchEngagement();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting payment:', error);
      }
    }
  };

  // Note CRUD functions
  const createNote = async (noteData: {
    note_type: string;
    subject: string;
    content: string;
    is_internal: boolean;
    occurred_at: string;
  }) => {
    if (!id || !userProfile) return;

    try {
      setNoteUpdating('creating');

      const { error } = await supabase
        .from('project_notes')
        .insert({
          project_id: id,
          note_type: noteData.note_type,
          subject: noteData.subject,
          content: noteData.content,
          is_internal: noteData.is_internal,
          occurred_at: noteData.occurred_at,
          created_by: userProfile.id,
        });

      if (error) throw error;

      // Refresh notes
      const { data: notesData } = await supabase
        .from('project_notes')
        .select('*, creator:platform_users!created_by(full_name)')
        .eq('project_id', id)
        .order('occurred_at', { ascending: false });

      if (notesData) setNotes(notesData);

      setShowAddNoteModal(false);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating note:', error);
      }
    } finally {
      setNoteUpdating(null);
    }
  };

  const updateNote = async (noteId: string, noteData: {
    note_type: string;
    subject: string;
    content: string;
    is_internal: boolean;
    occurred_at: string;
  }) => {
    if (!id) return;

    try {
      setNoteUpdating(noteId);

      const { error } = await supabase
        .from('project_notes')
        .update({
          note_type: noteData.note_type,
          subject: noteData.subject,
          content: noteData.content,
          is_internal: noteData.is_internal,
          occurred_at: noteData.occurred_at,
        })
        .eq('id', noteId);

      if (error) throw error;

      // Update local state
      setNotes(prev => prev.map(n =>
        n.id === noteId
          ? { ...n, ...noteData }
          : n
      ));

      setEditingNote(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating note:', error);
      }
    } finally {
      setNoteUpdating(null);
    }
  };

  const deleteNote = async (noteId: string) => {
    if (!id) return;

    try {
      setNoteUpdating(noteId);

      const { error } = await supabase
        .from('project_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev => prev.filter(n => n.id !== noteId));
      setDeleteConfirmNote(null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error deleting note:', error);
      }
    } finally {
      setNoteUpdating(null);
    }
  };

  // Get primary client
  const primaryClient = engagement?.project_parties?.find(pp => pp.is_primary)?.party;

  // Get phases from template or use default
  const phases = engagement?.template?.phases || [
    { number: 1, name: 'Phase 1' },
    { number: 2, name: 'Phase 2' },
  ].slice(0, engagement?.total_phases || 2);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!engagement) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FolderIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Engagement not found</h3>
          <p className="text-gray-500 mt-2">The engagement you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/portal/engagements')}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Return to Pipeline
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/portal/engagements')}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">{engagement.name}</h1>
                <StatusBadge status={engagement.status} />
              </div>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                {engagement.project_number && (
                  <span className="font-mono">{engagement.project_number}</span>
                )}
                {primaryClient && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="flex items-center gap-1">
                      <UserIcon className="h-4 w-4" />
                      {primaryClient.name}
                    </span>
                  </>
                )}
                {engagement.assignee && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="flex items-center gap-1">
                      <PencilSquareIcon className="h-4 w-4" />
                      {engagement.assignee.full_name}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Completion percentage */}
            <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
              <span className="text-sm text-gray-600">Progress:</span>
              <div className="w-24 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${tasks.length > 0
                      ? Math.round((tasks.filter(t => t.status === 'complete').length / tasks.length) * 100)
                      : 0}%`
                  }}
                />
              </div>
              <span className="text-sm font-medium text-gray-900">
                {tasks.length > 0
                  ? Math.round((tasks.filter(t => t.status === 'complete').length / tasks.length) * 100)
                  : 0}%
              </span>
            </div>

            {/* Action buttons */}
            <button
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add Note
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4 border-t border-gray-100 pt-4">
          <nav className="flex gap-6">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 pb-2 text-sm font-medium border-b-2 transition-colors
                    ${isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Progress Stepper */}
            <ProgressStepper
              currentPhase={engagement.current_phase}
              totalPhases={engagement.total_phases}
              phases={phases}
            />

            {/* Quick Stats */}
            <QuickStats
              engagement={engagement}
              tasks={tasks}
              documents={documents}
              payments={payments}
              onStatClick={(tab) => setActiveTab(tab)}
            />

            {/* Two column layout for actions and dates */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <NextActions
                  tasks={tasks}
                  onTaskClick={(taskId) => {
                    // Find the task to get its phase
                    const task = tasks.find(t => t.id === taskId);
                    if (task) {
                      // Expand the phase containing this task
                      setCollapsedPhases(prev => {
                        const next = new Set(prev);
                        next.delete(task.phase);
                        return next;
                      });
                    }
                    setActiveTab('plan');
                    // Scroll to task after tab switch
                    setTimeout(() => {
                      const taskElement = document.getElementById(`task-${taskId}`);
                      if (taskElement) {
                        taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        taskElement.classList.add('ring-2', 'ring-blue-500');
                        setTimeout(() => taskElement.classList.remove('ring-2', 'ring-blue-500'), 2000);
                      }
                    }, 100);
                  }}
                />
              </div>
              <div>
                <KeyDates engagement={engagement} />
              </div>
            </div>

            {/* Activity Feed */}
            <ActivityFeed notes={notes} />
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="space-y-4">
            {/* Phase sections */}
            {phases.map((phase) => {
              const phaseTasks = tasks.filter(t => t.phase === phase.number);
              const completedTasks = phaseTasks.filter(t => t.status === 'complete').length;
              const isCollapsed = collapsedPhases.has(phase.number);
              const phaseProgress = phaseTasks.length > 0
                ? Math.round((completedTasks / phaseTasks.length) * 100)
                : 0;

              return (
                <div key={phase.number} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Phase Header */}
                  <button
                    onClick={() => togglePhaseCollapse(phase.number)}
                    className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {isCollapsed ? (
                        <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                      )}
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
                          {phase.number}
                        </span>
                        <span className="font-semibold text-gray-900">{phase.name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${phaseProgress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 min-w-[60px]">
                          {completedTasks}/{phaseTasks.length} tasks
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Tasks */}
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {phaseTasks.length === 0 ? (
                        <div className="px-6 py-8 text-center text-gray-500">
                          <ClipboardDocumentListIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm">No tasks in this phase yet</p>
                        </div>
                      ) : (
                        phaseTasks.map((task) => {
                          const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'complete' && task.status !== 'cancelled' && task.status !== 'na';
                          const isUpdating = taskUpdating === task.id;

                          // Priority config
                          const priorityConfig: Record<string, { border: string; bg: string; label: string }> = {
                            urgent: { border: 'border-l-red-500', bg: 'bg-red-50', label: 'Urgent' },
                            high: { border: 'border-l-orange-500', bg: 'bg-orange-50', label: 'High' },
                            normal: { border: 'border-l-blue-500', bg: 'bg-blue-50', label: 'Normal' },
                            low: { border: 'border-l-gray-400', bg: 'bg-gray-50', label: 'Low' },
                          };

                          // Owner config
                          const ownerConfig: Record<string, { icon: React.ComponentType<any>; bg: string; text: string; label: string }> = {
                            advisor: { icon: PencilSquareIcon, bg: 'bg-blue-100', text: 'text-blue-700', label: 'Advisor' },
                            client: { icon: UserIcon, bg: 'bg-purple-100', text: 'text-purple-700', label: 'Client' },
                            legal: { icon: ScaleIcon, bg: 'bg-amber-100', text: 'text-amber-700', label: 'Legal' },
                            accountant: { icon: CalculatorIcon, bg: 'bg-green-100', text: 'text-green-700', label: 'Accountant' },
                            external: { icon: UserGroupIcon, bg: 'bg-gray-100', text: 'text-gray-700', label: 'External' },
                            system: { icon: CogIcon, bg: 'bg-slate-100', text: 'text-slate-700', label: 'System' },
                          };

                          // Status config
                          const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
                            not_started: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Not Started' },
                            in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'In Progress' },
                            blocked: { bg: 'bg-red-100', text: 'text-red-700', label: 'Blocked' },
                            complete: { bg: 'bg-green-100', text: 'text-green-700', label: 'Complete' },
                            cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Cancelled' },
                            na: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'N/A' },
                          };

                          const priority = priorityConfig[task.priority] || priorityConfig.normal;
                          const owner = ownerConfig[task.owner_type] || ownerConfig.advisor;
                          const status = statusConfig[task.status] || statusConfig.not_started;
                          const OwnerIcon = owner.icon;

                          return (
                            <div
                              key={task.id}
                              id={`task-${task.id}`}
                              className={`
                                px-6 py-4 border-l-4 transition-all
                                ${priority.border}
                                ${task.status === 'complete' ? 'bg-gray-50 opacity-70' : ''}
                                ${task.status === 'cancelled' || task.status === 'na' ? 'bg-gray-50 opacity-60' : ''}
                                hover:bg-gray-50
                              `}
                            >
                              <div className="flex items-start gap-4">
                                {/* Checkbox */}
                                <button
                                  onClick={() => updateTaskStatus(
                                    task.id,
                                    task.status === 'complete' ? 'not_started' : 'complete'
                                  )}
                                  disabled={isUpdating}
                                  className={`
                                    mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                                    ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                    ${task.status === 'complete'
                                      ? 'bg-green-500 border-green-500 text-white'
                                      : 'border-gray-300 hover:border-green-400'
                                    }
                                  `}
                                >
                                  {isUpdating ? (
                                    <ArrowPathIcon className="h-3 w-3 animate-spin" />
                                  ) : task.status === 'complete' ? (
                                    <CheckCircleIcon className="h-3 w-3" />
                                  ) : null}
                                </button>

                                {/* Task content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                      <h4 className={`
                                        font-medium
                                        ${task.status === 'complete' ? 'text-gray-500 line-through' : 'text-gray-900'}
                                      `}>
                                        {task.title}
                                      </h4>
                                      {task.description && (
                                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                          {task.description}
                                        </p>
                                      )}

                                      {/* Task metadata */}
                                      <div className="flex flex-wrap items-center gap-2 mt-2">
                                        {/* Owner badge */}
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${owner.bg} ${owner.text}`}>
                                          <OwnerIcon className="h-3 w-3" />
                                          {owner.label}
                                        </span>

                                        {/* Status dropdown */}
                                        <select
                                          value={task.status}
                                          onChange={(e) => updateTaskStatus(task.id, e.target.value as Task['status'])}
                                          disabled={isUpdating}
                                          className={`
                                            text-xs font-medium px-2 py-0.5 rounded-full border-0 cursor-pointer
                                            ${status.bg} ${status.text}
                                          `}
                                        >
                                          <option value="not_started">Not Started</option>
                                          <option value="in_progress">In Progress</option>
                                          <option value="blocked">Blocked</option>
                                          <option value="complete">Complete</option>
                                          <option value="cancelled">Cancelled</option>
                                          <option value="na">N/A</option>
                                        </select>

                                        {/* Priority badge */}
                                        {task.priority !== 'normal' && (
                                          <span className={`
                                            inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
                                            ${task.priority === 'urgent' ? 'bg-red-100 text-red-700' : ''}
                                            ${task.priority === 'high' ? 'bg-orange-100 text-orange-700' : ''}
                                            ${task.priority === 'low' ? 'bg-gray-100 text-gray-600' : ''}
                                          `}>
                                            {task.priority === 'urgent' && <ExclamationTriangleSolidIcon className="h-3 w-3" />}
                                            {priority.label}
                                          </span>
                                        )}

                                        {/* Due date */}
                                        {task.due_date && (
                                          <span className={`
                                            inline-flex items-center gap-1 text-xs
                                            ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}
                                          `}>
                                            <CalendarDaysIcon className={`h-3 w-3 ${isOverdue ? 'text-red-500' : ''}`} />
                                            {new Date(task.due_date).toLocaleDateString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                            })}
                                            {isOverdue && (
                                              <ExclamationCircleIcon className="h-3 w-3 text-red-500" />
                                            )}
                                          </span>
                                        )}

                                        {/* Blocked reason indicator */}
                                        {task.status === 'blocked' && task.blocked_reason && (
                                          <span className="inline-flex items-center gap-1 text-xs text-red-600" title={task.blocked_reason}>
                                            <ExclamationTriangleIcon className="h-3 w-3" />
                                            Blocked
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => setEditingTask(task)}
                                        className="p-1.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
                                        title="Edit task"
                                      >
                                        <PencilSquareIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => setDeleteConfirmTask(task)}
                                        className="p-1.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                                        title="Delete task"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Add task button */}
                      <button
                        onClick={() => {
                          setAddTaskPhase(phase.number);
                          setShowAddTaskModal(true);
                        }}
                        className="w-full px-6 py-3 flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add task to {phase.name}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Empty state if no phases */}
            {phases.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No phases configured</h3>
                <p className="text-sm text-gray-500 mt-2">This engagement needs a template with phases.</p>
              </div>
            )}
          </div>
        )}

        {/* Add Task Modal */}
        {showAddTaskModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddTaskModal(false)} />
            <div className="relative min-h-screen flex items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add New Task</h3>
                  <button
                    onClick={() => setShowAddTaskModal(false)}
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    createTask({
                      title: formData.get('title') as string,
                      description: formData.get('description') as string || null,
                      phase: addTaskPhase,
                      owner_type: formData.get('owner_type') as Task['owner_type'],
                      priority: formData.get('priority') as Task['priority'],
                      due_date: formData.get('due_date') as string || null,
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter task title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      rows={2}
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                      <select
                        name="owner_type"
                        defaultValue="advisor"
                        className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="advisor">Advisor</option>
                        <option value="client">Client</option>
                        <option value="legal">Legal</option>
                        <option value="accountant">Accountant</option>
                        <option value="external">External</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        name="priority"
                        defaultValue="normal"
                        className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <input
                      type="date"
                      name="due_date"
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddTaskModal(false)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Add Task
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Task Modal */}
        {editingTask && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setEditingTask(null)} />
            <div className="relative min-h-screen flex items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Task</h3>
                  <button
                    onClick={() => setEditingTask(null)}
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    updateTask(editingTask.id, {
                      title: formData.get('title') as string,
                      description: formData.get('description') as string || null,
                      owner_type: formData.get('owner_type') as Task['owner_type'],
                      status: formData.get('status') as Task['status'],
                      priority: formData.get('priority') as Task['priority'],
                      due_date: formData.get('due_date') as string || null,
                      notes: formData.get('notes') as string || null,
                      blocked_reason: formData.get('blocked_reason') as string || null,
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      defaultValue={editingTask.title}
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      name="description"
                      rows={2}
                      defaultValue={editingTask.description || ''}
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                      <select
                        name="owner_type"
                        defaultValue={editingTask.owner_type}
                        className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="advisor">Advisor</option>
                        <option value="client">Client</option>
                        <option value="legal">Legal</option>
                        <option value="accountant">Accountant</option>
                        <option value="external">External</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        name="status"
                        defaultValue={editingTask.status}
                        className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="blocked">Blocked</option>
                        <option value="complete">Complete</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="na">N/A</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                      <select
                        name="priority"
                        defaultValue={editingTask.priority}
                        className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="low">Low</option>
                        <option value="normal">Normal</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        name="due_date"
                        defaultValue={editingTask.due_date || ''}
                        className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      name="notes"
                      rows={2}
                      defaultValue={editingTask.notes || ''}
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Internal notes about this task"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Blocked Reason</label>
                    <input
                      type="text"
                      name="blocked_reason"
                      defaultValue={editingTask.blocked_reason || ''}
                      className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Why is this task blocked?"
                    />
                  </div>
                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setEditingTask(null)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmTask && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteConfirmTask(null)} />
            <div className="relative min-h-screen flex items-center justify-center p-4">
              <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-full bg-red-100">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Task</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete "<span className="font-medium">{deleteConfirmTask.title}</span>"?
                  This action cannot be undone.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirmTask(null)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => deleteTask(deleteConfirmTask.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Delete Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-6">
            {/* From Client Section */}
            {(() => {
              const fromClientDocs = documents.filter(d => d.direction === 'from_client');
              const isCollapsed = collapsedDocSections.has('from_client');
              const receivedCount = fromClientDocs.filter(d =>
                ['received', 'reviewed', 'approved'].includes(d.status) && d.status !== 'na'
              ).length;
              const requiredCount = fromClientDocs.filter(d => d.is_required && d.status !== 'na').length;
              const progress = requiredCount > 0 ? Math.round((receivedCount / requiredCount) * 100) : 100;

              // Status config for documents
              const docStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
                pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
                requested: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Requested' },
                received: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Received' },
                reviewed: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Reviewed' },
                approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
                rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
                delivered: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Delivered' },
                na: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'N/A' },
              };

              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleDocSectionCollapse('from_client')}
                    className="w-full px-6 py-4 flex items-center justify-between bg-purple-50 hover:bg-purple-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {isCollapsed ? (
                        <ChevronRightIcon className="h-5 w-5 text-purple-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-purple-600" />
                      )}
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-100">
                          <InboxArrowDownIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <span className="font-semibold text-gray-900">From Client</span>
                        <span className="text-sm text-gray-500">Documents we need from the client</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-purple-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 min-w-[60px]">
                          {receivedCount}/{requiredCount}
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Documents List */}
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {fromClientDocs.length === 0 ? (
                        <div className="px-6 py-8 text-center text-gray-500">
                          <DocumentTextIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm">No documents required from client</p>
                        </div>
                      ) : (
                        fromClientDocs.map((doc) => {
                          const isOverdue = doc.due_date && new Date(doc.due_date) < new Date() &&
                            !['received', 'reviewed', 'approved', 'na'].includes(doc.status);
                          const isUpdating = documentUpdating === doc.id;
                          const statusStyle = docStatusConfig[doc.status] || docStatusConfig.pending;

                          return (
                            <div
                              key={doc.id}
                              className={`
                                px-6 py-4 transition-all
                                ${doc.status === 'na' ? 'bg-gray-50 opacity-60' : ''}
                                ${isOverdue ? 'border-l-4 border-l-red-500' : ''}
                                hover:bg-gray-50
                              `}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className={`font-medium ${doc.status === 'na' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                      {doc.document_name}
                                    </h4>
                                    {doc.is_required && doc.status !== 'na' && (
                                      <span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                                        Required
                                      </span>
                                    )}
                                    {!doc.is_required && (
                                      <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded">
                                        Optional
                                      </span>
                                    )}
                                  </div>

                                  {/* Document metadata */}
                                  <div className="flex flex-wrap items-center gap-2 mt-2">
                                    {/* Status dropdown */}
                                    <select
                                      value={doc.status}
                                      onChange={(e) => updateDocumentStatus(doc.id, e.target.value as ProjectDocument['status'])}
                                      disabled={isUpdating}
                                      className={`
                                        text-xs font-medium px-2 py-0.5 rounded-full border-0 cursor-pointer
                                        ${statusStyle.bg} ${statusStyle.text}
                                      `}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="requested">Requested</option>
                                      <option value="received">Received</option>
                                      <option value="reviewed">Reviewed</option>
                                      <option value="approved">Approved</option>
                                      <option value="rejected">Rejected</option>
                                      <option value="na">N/A</option>
                                    </select>

                                    {/* Due date */}
                                    {doc.due_date && (
                                      <span className={`
                                        inline-flex items-center gap-1 text-xs
                                        ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}
                                      `}>
                                        <CalendarDaysIcon className={`h-3 w-3 ${isOverdue ? 'text-red-500' : ''}`} />
                                        Due: {new Date(doc.due_date).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                        })}
                                        {isOverdue && <ExclamationCircleIcon className="h-3 w-3 text-red-500" />}
                                      </span>
                                    )}

                                    {/* File info if uploaded */}
                                    {doc.file_name && (
                                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                        <DocumentTextIcon className="h-3 w-3" />
                                        {doc.file_name}
                                        {doc.file_size && ` (${Math.round(doc.file_size / 1024)}KB)`}
                                      </span>
                                    )}
                                  </div>

                                  {/* Notes */}
                                  {doc.notes && (
                                    <p className="text-sm text-gray-500 mt-2 italic">
                                      "{doc.notes}"
                                    </p>
                                  )}

                                  {/* Rejection reason */}
                                  {doc.status === 'rejected' && doc.rejection_reason && (
                                    <p className="text-sm text-red-600 mt-2">
                                      Rejection reason: {doc.rejection_reason}
                                    </p>
                                  )}
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center gap-2">
                                  {/* Upload button */}
                                  {!doc.file_path && doc.status !== 'na' && (
                                    <button
                                      onClick={() => setUploadingDocument(doc)}
                                      disabled={isUpdating}
                                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                      <ArrowUpTrayIcon className="h-4 w-4" />
                                      Upload
                                    </button>
                                  )}

                                  {/* Download button */}
                                  {doc.file_path && (
                                    <button
                                      onClick={() => downloadDocument(doc)}
                                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-1.5"
                                    >
                                      <ArrowDownTrayIcon className="h-4 w-4" />
                                      Download
                                    </button>
                                  )}

                                  {/* Re-upload button */}
                                  {doc.file_path && doc.status !== 'na' && (
                                    <button
                                      onClick={() => setUploadingDocument(doc)}
                                      disabled={isUpdating}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                      title="Upload new version"
                                    >
                                      <ArrowUpTrayIcon className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* Mark N/A button */}
                                  {doc.status !== 'na' && !doc.file_path && (
                                    <button
                                      onClick={() => updateDocumentStatus(doc.id, 'na')}
                                      disabled={isUpdating}
                                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                      title="Mark as N/A"
                                    >
                                      <NoSymbolIcon className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* Edit notes button */}
                                  <button
                                    onClick={() => setEditingDocument(doc)}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    title="Edit notes"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Add document button */}
                      <button
                        onClick={() => {
                          setAddDocumentDirection('from_client');
                          setShowAddDocumentModal(true);
                        }}
                        className="w-full px-6 py-3 flex items-center gap-2 text-sm text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add document request
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* To Client Section */}
            {(() => {
              const toClientDocs = documents.filter(d => d.direction === 'to_client');
              const isCollapsed = collapsedDocSections.has('to_client');
              const deliveredCount = toClientDocs.filter(d => d.status === 'delivered').length;
              const totalCount = toClientDocs.filter(d => d.status !== 'na').length;
              const progress = totalCount > 0 ? Math.round((deliveredCount / totalCount) * 100) : 100;

              // Status config for documents
              const docStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
                pending: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
                requested: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Requested' },
                received: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Received' },
                reviewed: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Reviewed' },
                approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
                rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
                delivered: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Delivered' },
                na: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'N/A' },
              };

              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Section Header */}
                  <button
                    onClick={() => toggleDocSectionCollapse('to_client')}
                    className="w-full px-6 py-4 flex items-center justify-between bg-emerald-50 hover:bg-emerald-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {isCollapsed ? (
                        <ChevronRightIcon className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-emerald-600" />
                      )}
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100">
                          <PaperAirplaneIcon className="h-5 w-5 text-emerald-600" />
                        </div>
                        <span className="font-semibold text-gray-900">To Client</span>
                        <span className="text-sm text-gray-500">Documents we deliver to the client</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-emerald-200 rounded-full h-2">
                          <div
                            className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 min-w-[60px]">
                          {deliveredCount}/{totalCount}
                        </span>
                      </div>
                    </div>
                  </button>

                  {/* Documents List */}
                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {toClientDocs.length === 0 ? (
                        <div className="px-6 py-8 text-center text-gray-500">
                          <DocumentTextIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p className="text-sm">No documents to deliver to client</p>
                        </div>
                      ) : (
                        toClientDocs.map((doc) => {
                          const isUpdating = documentUpdating === doc.id;
                          const statusStyle = docStatusConfig[doc.status] || docStatusConfig.pending;

                          return (
                            <div
                              key={doc.id}
                              className={`
                                px-6 py-4 transition-all
                                ${doc.status === 'na' ? 'bg-gray-50 opacity-60' : ''}
                                hover:bg-gray-50
                              `}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className={`font-medium ${doc.status === 'na' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                      {doc.document_name}
                                    </h4>
                                  </div>

                                  {/* Document metadata */}
                                  <div className="flex flex-wrap items-center gap-2 mt-2">
                                    {/* Status dropdown */}
                                    <select
                                      value={doc.status}
                                      onChange={(e) => updateDocumentStatus(doc.id, e.target.value as ProjectDocument['status'])}
                                      disabled={isUpdating}
                                      className={`
                                        text-xs font-medium px-2 py-0.5 rounded-full border-0 cursor-pointer
                                        ${statusStyle.bg} ${statusStyle.text}
                                      `}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="reviewed">In Review</option>
                                      <option value="approved">Approved</option>
                                      <option value="delivered">Delivered</option>
                                      <option value="na">N/A</option>
                                    </select>

                                    {/* File info if uploaded */}
                                    {doc.file_name && (
                                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                        <DocumentTextIcon className="h-3 w-3" />
                                        {doc.file_name}
                                        {doc.file_size && ` (${Math.round(doc.file_size / 1024)}KB)`}
                                      </span>
                                    )}
                                  </div>

                                  {/* Notes */}
                                  {doc.notes && (
                                    <p className="text-sm text-gray-500 mt-2 italic">
                                      "{doc.notes}"
                                    </p>
                                  )}
                                </div>

                                {/* Action buttons */}
                                <div className="flex items-center gap-2">
                                  {/* Upload button */}
                                  {!doc.file_path && doc.status !== 'na' && (
                                    <button
                                      onClick={() => setUploadingDocument(doc)}
                                      disabled={isUpdating}
                                      className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1.5"
                                    >
                                      <ArrowUpTrayIcon className="h-4 w-4" />
                                      Upload
                                    </button>
                                  )}

                                  {/* Download button */}
                                  {doc.file_path && (
                                    <button
                                      onClick={() => downloadDocument(doc)}
                                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center gap-1.5"
                                    >
                                      <ArrowDownTrayIcon className="h-4 w-4" />
                                      Download
                                    </button>
                                  )}

                                  {/* Re-upload button */}
                                  {doc.file_path && doc.status !== 'na' && (
                                    <button
                                      onClick={() => setUploadingDocument(doc)}
                                      disabled={isUpdating}
                                      className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                      title="Upload new version"
                                    >
                                      <ArrowUpTrayIcon className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* Mark N/A button */}
                                  {doc.status !== 'na' && !doc.file_path && (
                                    <button
                                      onClick={() => updateDocumentStatus(doc.id, 'na')}
                                      disabled={isUpdating}
                                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                      title="Mark as N/A"
                                    >
                                      <NoSymbolIcon className="h-4 w-4" />
                                    </button>
                                  )}

                                  {/* Edit notes button */}
                                  <button
                                    onClick={() => setEditingDocument(doc)}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    title="Edit notes"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}

                      {/* Add document button */}
                      <button
                        onClick={() => {
                          setAddDocumentDirection('to_client');
                          setShowAddDocumentModal(true);
                        }}
                        className="w-full px-6 py-3 flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4" />
                        Add deliverable
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Upload Modal */}
            {uploadingDocument && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setUploadingDocument(null)} />
                <div className="relative min-h-screen flex items-center justify-center p-4">
                  <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Upload Document</h3>
                      <button
                        onClick={() => setUploadingDocument(null)}
                        className="p-1 rounded hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm text-gray-600">
                        Uploading for: <span className="font-medium">{uploadingDocument.label}</span>
                      </p>
                    </div>

                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <ArrowUpTrayIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                      <p className="text-sm text-gray-600 mb-2">
                        Drag and drop your file here, or click to browse
                      </p>
                      <p className="text-xs text-gray-500 mb-4">
                        PDF, Word, Excel, CSV, or images (max 10MB)
                      </p>
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.gif"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(uploadingDocument.id, file);
                          }
                        }}
                      />
                      <label
                        htmlFor="file-upload"
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 cursor-pointer inline-block"
                      >
                        Choose File
                      </label>
                    </div>

                    {documentUpdating === uploadingDocument.id && (
                      <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        Uploading...
                      </div>
                    )}

                    <div className="mt-6 flex gap-3 justify-end">
                      <button
                        onClick={() => setUploadingDocument(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Document Modal */}
            {editingDocument && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setEditingDocument(null)} />
                <div className="relative min-h-screen flex items-center justify-center p-4">
                  <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Edit Document</h3>
                      <button
                        onClick={() => setEditingDocument(null)}
                        className="p-1 rounded hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const notes = formData.get('notes') as string;
                        updateDocumentNotes(editingDocument.id, notes);
                        setEditingDocument(null);
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document
                        </label>
                        <p className="text-sm text-gray-900">{editingDocument.label}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          rows={3}
                          defaultValue={editingDocument.notes || ''}
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Add notes about this document..."
                        />
                      </div>
                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => setEditingDocument(null)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Save
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Add Document Modal */}
            {showAddDocumentModal && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddDocumentModal(false)} />
                <div className="relative min-h-screen flex items-center justify-center p-4">
                  <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {addDocumentDirection === 'from_client' ? 'Add Document Request' : 'Add Deliverable'}
                      </h3>
                      <button
                        onClick={() => setShowAddDocumentModal(false)}
                        className="p-1 rounded hover:bg-gray-100"
                      >
                        <XMarkIcon className="h-5 w-5 text-gray-500" />
                      </button>
                    </div>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        createDocument({
                          document_name: formData.get('document_name') as string,
                          document_type: formData.get('document_type') as string,
                          direction: addDocumentDirection,
                          is_required: formData.get('is_required') === 'true',
                          due_date: formData.get('due_date') as string || null,
                          notes: formData.get('notes') as string || null,
                        });
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="document_name"
                          required
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., Notice of Assessment"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Document Type
                        </label>
                        <select
                          name="document_type"
                          defaultValue="other"
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="noa">Notice of Assessment</option>
                          <option value="corp_financials">Corporate Financials</option>
                          <option value="articles">Articles of Incorporation</option>
                          <option value="engagement_letter">Engagement Letter</option>
                          <option value="id">ID Document</option>
                          <option value="contract">Contract</option>
                          <option value="report">Report</option>
                          <option value="proposal">Proposal</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      {addDocumentDirection === 'from_client' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Required?</label>
                          <select
                            name="is_required"
                            defaultValue="true"
                            className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="true">Yes, required</option>
                            <option value="false">No, optional</option>
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          name="due_date"
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          name="notes"
                          rows={2}
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Optional notes..."
                        />
                      </div>
                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => setShowAddDocumentModal(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Add Document
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'money' && (
          <div className="space-y-6">
            {/* Fee Summary Cards */}
            {(() => {
              const totalFee = engagement?.fee_total || 0;
              const depositAmount = engagement?.fee_deposit || 0;
              const paidAmount = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
              const balanceDue = totalFee - paidAmount;
              const paymentProgress = totalFee > 0 ? Math.round((paidAmount / totalFee) * 100) : 0;

              return (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Total Fee */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Total Fee</span>
                      <BanknotesIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <p className="mt-2 text-2xl font-bold text-gray-900">
                      ${totalFee.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{engagement?.fee_currency || 'CAD'}</p>
                  </div>

                  {/* Deposit Required */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Deposit Required</span>
                      <ArrowDownTrayIcon className="h-5 w-5 text-blue-400" />
                    </div>
                    <p className="mt-2 text-2xl font-bold text-blue-600">
                      ${depositAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{totalFee > 0 ? `${Math.round((depositAmount / totalFee) * 100)}% of total` : '0%'}</p>
                  </div>

                  {/* Amount Paid */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Amount Paid</span>
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                    </div>
                    <p className="mt-2 text-2xl font-bold text-green-600">
                      ${paidAmount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{paymentProgress}% complete</p>
                  </div>

                  {/* Balance Due */}
                  <div className={`bg-white rounded-lg shadow-sm border p-5 ${balanceDue > 0 ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500">Balance Due</span>
                      {balanceDue > 0 ? (
                        <ExclamationCircleIcon className="h-5 w-5 text-amber-500" />
                      ) : (
                        <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <p className={`mt-2 text-2xl font-bold ${balanceDue > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                      ${balanceDue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{balanceDue <= 0 ? 'Fully paid' : 'Outstanding'}</p>
                  </div>
                </div>
              );
            })()}

            {/* Payment Progress Bar */}
            {(() => {
              const totalFee = engagement?.fee_total || 0;
              const paidAmount = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + p.amount, 0);
              const paymentProgress = totalFee > 0 ? Math.round((paidAmount / totalFee) * 100) : 0;

              return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Payment Progress</span>
                    <span className="text-sm font-medium text-gray-900">{paymentProgress}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                      style={{ width: `${paymentProgress}%` }}
                    />
                  </div>
                </div>
              );
            })()}

            {/* Payments List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BanknotesIcon className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold text-gray-900">Payment Schedule</h3>
                </div>
                <button
                  onClick={() => setShowAddPaymentModal(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Payment
                </button>
              </div>

              {payments.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <BanknotesIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm">No payments scheduled</p>
                  <button
                    onClick={() => setShowAddPaymentModal(true)}
                    className="mt-2 text-sm text-green-600 hover:text-green-700"
                  >
                    Add the first payment
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {payments
                    .sort((a, b) => {
                      // Sort by type order: deposit, balance, milestone, additional, refund
                      const typeOrder = { deposit: 0, balance: 1, milestone: 2, additional: 3, refund: 4 };
                      return (typeOrder[a.payment_type] || 5) - (typeOrder[b.payment_type] || 5);
                    })
                    .map(payment => {
                      const isOverdue = payment.status === 'pending' && payment.due_date && new Date(payment.due_date) < new Date();
                      const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
                        pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
                        invoiced: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Invoiced' },
                        paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
                        overdue: { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' },
                        waived: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Waived' },
                        refunded: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Refunded' },
                      };
                      const displayStatus = isOverdue ? 'overdue' : payment.status;
                      const config = statusConfig[displayStatus] || statusConfig.pending;

                      const typeLabels: Record<string, string> = {
                        deposit: 'Deposit',
                        balance: 'Balance',
                        milestone: 'Milestone',
                        additional: 'Additional Fee',
                        refund: 'Refund',
                      };

                      const methodLabels: Record<string, string> = {
                        etransfer: 'E-Transfer',
                        cheque: 'Cheque',
                        wire: 'Wire Transfer',
                        credit_card: 'Credit Card',
                        cash: 'Cash',
                        other: 'Other',
                      };

                      return (
                        <div
                          key={payment.id}
                          className={`p-4 hover:bg-gray-50 transition-colors ${isOverdue ? 'bg-red-50' : ''}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                  payment.payment_type === 'deposit' ? 'bg-blue-100 text-blue-800' :
                                  payment.payment_type === 'balance' ? 'bg-indigo-100 text-indigo-800' :
                                  payment.payment_type === 'refund' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {typeLabels[payment.payment_type] || payment.payment_type}
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
                                  {isOverdue && <ExclamationTriangleSolidIcon className="h-3 w-3 mr-1" />}
                                  {config.label}
                                </span>
                                {paymentUpdating === payment.id && (
                                  <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
                                )}
                              </div>
                              {payment.description && (
                                <p className="mt-1 text-sm text-gray-600">{payment.description}</p>
                              )}
                              <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                                {payment.due_date && (
                                  <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                                    <CalendarDaysIcon className="h-3.5 w-3.5" />
                                    Due: {new Date(payment.due_date).toLocaleDateString('en-CA')}
                                  </span>
                                )}
                                {payment.paid_at && (
                                  <span className="flex items-center gap-1 text-green-600">
                                    <CheckCircleSolidIcon className="h-3.5 w-3.5" />
                                    Paid: {new Date(payment.paid_at).toLocaleDateString('en-CA')}
                                  </span>
                                )}
                                {payment.payment_method && (
                                  <span className="flex items-center gap-1">
                                    <BanknotesIcon className="h-3.5 w-3.5" />
                                    {methodLabels[payment.payment_method] || payment.payment_method}
                                  </span>
                                )}
                                {payment.reference_number && (
                                  <span className="flex items-center gap-1">
                                    Ref: {payment.reference_number}
                                  </span>
                                )}
                              </div>
                              {payment.notes && (
                                <p className="mt-2 text-xs text-gray-500 italic">{payment.notes}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-4">
                              <span className={`text-xl font-bold ${payment.payment_type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                                {payment.payment_type === 'refund' ? '-' : ''}${payment.amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                              </span>
                              <div className="flex items-center gap-1">
                                {payment.status !== 'paid' && payment.status !== 'waived' && payment.status !== 'refunded' && (
                                  <button
                                    onClick={() => setMarkPaidPayment(payment)}
                                    className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors"
                                    title="Mark as Paid"
                                  >
                                    <CheckCircleIcon className="h-5 w-5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => setEditingPayment(payment)}
                                  className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                  title="Edit Payment"
                                >
                                  <PencilSquareIcon className="h-5 w-5" />
                                </button>
                                {payment.payment_type === 'additional' && (
                                  <button
                                    onClick={() => {
                                      if (confirm('Are you sure you want to delete this payment?')) {
                                        deletePayment(payment.id);
                                      }
                                    }}
                                    className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                                    title="Delete Payment"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Additional Fees Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <ExclamationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Additional Fees</h4>
                  <p className="mt-1 text-sm text-blue-700">
                    Some services may incur additional costs not included in the project fee, such as legal fees, insurance premiums, or regulatory filings.
                    These are tracked separately and payable directly to the service provider.
                  </p>
                </div>
              </div>
            </div>

            {/* Mark as Paid Modal */}
            {markPaidPayment && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setMarkPaidPayment(null)} />
                <div className="flex min-h-full items-center justify-center p-4">
                  <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Mark Payment as Paid</h3>
                      <button onClick={() => setMarkPaidPayment(null)} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Amount</p>
                      <p className="text-2xl font-bold text-gray-900">${markPaidPayment.amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}</p>
                      {markPaidPayment.description && (
                        <p className="text-sm text-gray-500 mt-1">{markPaidPayment.description}</p>
                      )}
                    </div>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      markPaymentPaid(markPaidPayment.id, {
                        paid_at: formData.get('paid_at') as string || new Date().toISOString(),
                        payment_method: formData.get('payment_method') as Payment['payment_method'],
                        reference_number: formData.get('reference_number') as string,
                        notes: formData.get('notes') as string,
                      });
                    }} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="paid_at"
                          defaultValue={new Date().toISOString().split('T')[0]}
                          required
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Method <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="payment_method"
                          required
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="">Select method...</option>
                          <option value="etransfer">E-Transfer</option>
                          <option value="cheque">Cheque</option>
                          <option value="wire">Wire Transfer</option>
                          <option value="credit_card">Credit Card</option>
                          <option value="cash">Cash</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                        <input
                          type="text"
                          name="reference_number"
                          placeholder="Transaction ID, cheque number, etc."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          name="notes"
                          rows={2}
                          placeholder="Optional payment notes..."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => setMarkPaidPayment(null)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={paymentUpdating === markPaidPayment.id}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          {paymentUpdating === markPaidPayment.id && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                          Mark as Paid
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Payment Modal */}
            {editingPayment && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setEditingPayment(null)} />
                <div className="flex min-h-full items-center justify-center p-4">
                  <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Edit Payment</h3>
                      <button onClick={() => setEditingPayment(null)} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      updatePayment(editingPayment.id, {
                        description: formData.get('description') as string,
                        amount: parseFloat(formData.get('amount') as string) || 0,
                        due_date: formData.get('due_date') as string || null,
                        notes: formData.get('notes') as string,
                      });
                    }} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <input
                          type="text"
                          name="description"
                          defaultValue={editingPayment.description || ''}
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                        <input
                          type="number"
                          name="amount"
                          step="0.01"
                          min="0"
                          defaultValue={editingPayment.amount}
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          name="due_date"
                          defaultValue={editingPayment.due_date || ''}
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          name="notes"
                          rows={2}
                          defaultValue={editingPayment.notes || ''}
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => setEditingPayment(null)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Add Payment Modal */}
            {showAddPaymentModal && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddPaymentModal(false)} />
                <div className="flex min-h-full items-center justify-center p-4">
                  <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Add Payment</h3>
                      <button onClick={() => setShowAddPaymentModal(false)} className="text-gray-400 hover:text-gray-600">
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      createPayment({
                        payment_type: formData.get('payment_type') as Payment['payment_type'],
                        description: formData.get('description') as string,
                        amount: parseFloat(formData.get('amount') as string) || 0,
                        due_date: formData.get('due_date') as string || null,
                        notes: formData.get('notes') as string,
                      });
                    }} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Payment Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="payment_type"
                          required
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        >
                          <option value="additional">Additional Fee</option>
                          <option value="milestone">Milestone Payment</option>
                          <option value="deposit">Deposit</option>
                          <option value="balance">Balance</option>
                          <option value="refund">Refund</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="description"
                          required
                          placeholder="e.g., Legal fees, Insurance premium"
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Amount ($) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="amount"
                          step="0.01"
                          min="0"
                          required
                          placeholder="0.00"
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          name="due_date"
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <textarea
                          name="notes"
                          rows={2}
                          placeholder="Optional notes..."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <div className="flex gap-3 justify-end pt-4">
                        <button
                          type="button"
                          onClick={() => setShowAddPaymentModal(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                          Add Payment
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'worklog' && (
          <div className="space-y-6">
            {/* Work Log Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Work Log</h2>
                <span className="text-sm text-gray-500">{notes.length} entries</span>
              </div>
              <div className="flex items-center gap-3">
                {/* Type Filter */}
                <select
                  value={noteFilter}
                  onChange={(e) => setNoteFilter(e.target.value)}
                  className="rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="all">All Types</option>
                  <option value="meeting">Meetings</option>
                  <option value="call">Calls</option>
                  <option value="email">Emails</option>
                  <option value="internal">Internal Notes</option>
                  <option value="decision">Decisions</option>
                  <option value="milestone">Milestones</option>
                  <option value="update">Updates</option>
                  <option value="issue">Issues</option>
                  <option value="resolution">Resolutions</option>
                </select>
                {/* Add Note Button */}
                <button
                  onClick={() => setShowAddNoteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Entry
                </button>
              </div>
            </div>

            {/* Notes List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {(() => {
                const filteredNotes = noteFilter === 'all'
                  ? notes
                  : notes.filter(n => n.note_type === noteFilter);

                const noteTypeConfig: Record<string, { icon: React.ComponentType<any>; color: string; label: string }> = {
                  meeting: { icon: UserGroupIcon, color: 'bg-blue-100 text-blue-600', label: 'Meeting' },
                  call: { icon: ChatBubbleLeftEllipsisIcon, color: 'bg-green-100 text-green-600', label: 'Call' },
                  email: { icon: DocumentTextIcon, color: 'bg-purple-100 text-purple-600', label: 'Email' },
                  internal: { icon: PencilSquareIcon, color: 'bg-gray-100 text-gray-600', label: 'Internal' },
                  decision: { icon: CheckCircleIcon, color: 'bg-amber-100 text-amber-600', label: 'Decision' },
                  milestone: { icon: FolderIcon, color: 'bg-indigo-100 text-indigo-600', label: 'Milestone' },
                  update: { icon: ClockIcon, color: 'bg-cyan-100 text-cyan-600', label: 'Update' },
                  issue: { icon: ExclamationTriangleIcon, color: 'bg-red-100 text-red-600', label: 'Issue' },
                  resolution: { icon: CheckCircleSolidIcon, color: 'bg-green-100 text-green-600', label: 'Resolution' },
                };

                if (filteredNotes.length === 0) {
                  return (
                    <div className="text-center py-12 text-gray-500">
                      <ChatBubbleLeftEllipsisIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900">No entries yet</h3>
                      <p className="text-sm mt-1">Add a meeting note, call summary, or email record to start the work log.</p>
                    </div>
                  );
                }

                return (
                  <div className="divide-y divide-gray-200">
                    {filteredNotes.map((note) => {
                      const config = noteTypeConfig[note.note_type] || noteTypeConfig.update;
                      const Icon = config.icon;

                      return (
                        <div key={note.id} className="p-4 hover:bg-gray-50 transition-colors">
                          <div className="flex gap-4">
                            {/* Type Icon */}
                            <div className={`p-2 rounded-lg flex-shrink-0 ${config.color}`}>
                              <Icon className="h-5 w-5" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-medium text-gray-900">
                                      {note.subject || `${config.label} Note`}
                                    </h4>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${config.color}`}>
                                      {config.label}
                                    </span>
                                    {note.is_internal && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                        Internal Only
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
                                    {note.content}
                                  </p>
                                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                    <span>{note.creator?.full_name || 'System'}</span>
                                    <span>-</span>
                                    <span>
                                      {new Date(note.occurred_at).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                      })}
                                    </span>
                                  </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => setEditingNote(note)}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirmNote(note)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Add Note Modal */}
            {showAddNoteModal && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAddNoteModal(false)} />
                <div className="flex items-center justify-center min-h-screen p-4">
                  <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Add Work Log Entry</h3>
                      <button
                        onClick={() => setShowAddNoteModal(false)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        createNote({
                          note_type: formData.get('note_type') as string,
                          subject: formData.get('subject') as string,
                          content: formData.get('content') as string,
                          is_internal: formData.get('is_internal') === 'on',
                          occurred_at: formData.get('occurred_at') as string || new Date().toISOString(),
                        });
                      }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="note_type"
                            required
                            className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="meeting">Meeting</option>
                            <option value="call">Call</option>
                            <option value="email">Email</option>
                            <option value="internal">Internal Note</option>
                            <option value="decision">Decision</option>
                            <option value="milestone">Milestone</option>
                            <option value="update">Update</option>
                            <option value="issue">Issue</option>
                            <option value="resolution">Resolution</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date/Time
                          </label>
                          <input
                            type="datetime-local"
                            name="occurred_at"
                            defaultValue={new Date().toISOString().slice(0, 16)}
                            className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          placeholder="Brief summary..."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="content"
                          required
                          rows={4}
                          placeholder="Details of the meeting, call, or note..."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="is_internal"
                          id="is_internal"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="is_internal" className="text-sm text-gray-700">
                          Internal only (hidden from client view)
                        </label>
                      </div>

                      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setShowAddNoteModal(false)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={noteUpdating === 'creating'}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {noteUpdating === 'creating' ? 'Adding...' : 'Add Entry'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Edit Note Modal */}
            {editingNote && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setEditingNote(null)} />
                <div className="flex items-center justify-center min-h-screen p-4">
                  <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Edit Work Log Entry</h3>
                      <button
                        onClick={() => setEditingNote(null)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        updateNote(editingNote.id, {
                          note_type: formData.get('note_type') as string,
                          subject: formData.get('subject') as string,
                          content: formData.get('content') as string,
                          is_internal: formData.get('is_internal') === 'on',
                          occurred_at: formData.get('occurred_at') as string || editingNote.occurred_at,
                        });
                      }}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type <span className="text-red-500">*</span>
                          </label>
                          <select
                            name="note_type"
                            defaultValue={editingNote.note_type}
                            required
                            className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="meeting">Meeting</option>
                            <option value="call">Call</option>
                            <option value="email">Email</option>
                            <option value="internal">Internal Note</option>
                            <option value="decision">Decision</option>
                            <option value="milestone">Milestone</option>
                            <option value="update">Update</option>
                            <option value="issue">Issue</option>
                            <option value="resolution">Resolution</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date/Time
                          </label>
                          <input
                            type="datetime-local"
                            name="occurred_at"
                            defaultValue={editingNote.occurred_at.slice(0, 16)}
                            className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          defaultValue={editingNote.subject || ''}
                          placeholder="Brief summary..."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="content"
                          defaultValue={editingNote.content}
                          required
                          rows={4}
                          placeholder="Details of the meeting, call, or note..."
                          className="w-full rounded-md px-3 py-2 bg-white text-gray-900 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="is_internal"
                          id="edit_is_internal"
                          defaultChecked={editingNote.is_internal}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="edit_is_internal" className="text-sm text-gray-700">
                          Internal only (hidden from client view)
                        </label>
                      </div>

                      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setEditingNote(null)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={noteUpdating === editingNote.id}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {noteUpdating === editingNote.id ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmNote && (
              <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteConfirmNote(null)} />
                <div className="flex items-center justify-center min-h-screen p-4">
                  <div className="relative z-50 bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-red-100 rounded-full">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">Delete Entry</h3>
                    </div>

                    <p className="text-gray-600 mb-6">
                      Are you sure you want to delete this work log entry? This action cannot be undone.
                    </p>

                    <div className="p-3 bg-gray-50 rounded-lg mb-6">
                      <p className="text-sm font-medium text-gray-900">
                        {deleteConfirmNote.subject || `${deleteConfirmNote.note_type} Note`}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {deleteConfirmNote.content}
                      </p>
                    </div>

                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => setDeleteConfirmNote(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => deleteNote(deleteConfirmNote.id)}
                        disabled={noteUpdating === deleteConfirmNote.id}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        {noteUpdating === deleteConfirmNote.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EngagementDetail;
